import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";
import ServicesSlider from "@/pages/services/components/ServicesSlider";
import ServiceCards from "@/components/feature/ServiceCards";

const COMPANY_NAME = "NetBasis International";

const texts = {
  title: "Unsere Services",
  h1Text:
    "Umfassende kaufmännische und technische Dienstleistungen für Ihren nachhaltigen Geschäftserfolg. Wir begleiten ausländische ITK-Hersteller als erfahrener lokaler Partner bei der Erschließung neuer Märkte und bieten maßgeschneiderte Lösungen für jeden Schritt Ihrer Expansion.",
  introParagraphs: [
    "Wir von NetBasis International bieten ausländischen Herstellern aus der Telekommunikations- und IT-Branche ein breites Spektrum an kaufmännischen und technischen Dienstleistungen sowie Beratung und Schulung an, um sie auf Ihrem Weg in neue Märkte schnell und effizient zu unterstützen. Unser Fokus liegt dabei ganz klar auf der Erreichung Ihrer Wachstumsziele im gesamten deutschsprachigen Wirtschaftsraum (DACH-Region).",
    "Damit Sie schneller den von Ihnen gewünschten Themenbereich finden, haben wir unser Angebot in vier spezialisierte Sektionen unterteilt:",
    "Wenn Sie uns aktiv in Ihr neues Geschäft einbinden und uns reale Aufgaben durchführen lassen möchten, dann schauen Sie sich bei „Business Development\" unsere unterschiedlichen Services an und wählen Sie aus, was wir konkret für Sie tun können. Falls Sie uns als reine Berater an Ihrer Seite wissen möchten, dann sehen Sie sich bei „Consulting\" um. Wollen Sie uns in einem unserer Themengebiete „auf Zeit\" engagieren, dann klicken Sie einfach direkt auf „Interim Management\". Oder Sie möchten unser Know-how zu reinen Trainings- und Coachingzwecken abfragen, dann sehen Sie sich im entsprechenden Fachbereich um.",
  ],
  sectionTitle:
    "Gemeinsam zu operativer Exzellenz und nachhaltigem Markterfolg",
  belowText:
    "Was auch immer Ihre Idee ist, NetBasis International hilft Ihnen, die Ziele und Visionen Ihres Markteintrittsplans genau zu spezifizieren. Als Branchenexperten vor Ort assistieren wir Ihnen dabei, alle Prozesse permanent zu überprüfen und immer wieder agil an die individuellen Erfordernisse der neuen Märkte anzupassen. Wir verstehen uns dabei nicht als externer Dienstleister, sondern als loyale und vollintegrierte Mitarbeiter Ihres eigenen Teams, die eng mit Ihrem Projekt verbunden sind. Wir besitzen das feine Gespür und das fundierte Fachwissen für die lokalen Marktmechanismen, die zwingend erforderlich sind, um Ihr Auslandsvorhaben zu einem echten und dauerhaften Erfolg werden zu lassen. Mit dieser tiefen Verbundenheit und unserem klaren Fokus auf messbare Resultate sichern wir Schritt für Schritt Ihre starke und wettbewerbsfähige Marktpräsenz.",
  claim: "NetBasis International - drive to excellence with excellent drivers !",
};

const cardTexts = {
  titles: [
    "Business Development",
    "Consulting",
    "Interim Management",
    "Training & Coaching",
  ],
  descriptions: [
    "Sie suchen aktive Hilfe und möchten operative B2B-Jobs einfach abgeben? Dann wählen Sie aus unseren Services.",
    "Sie möchten uns als reine Berater an Ihrer Seite wissen? Dann sehen Sie sich einfach in unserem Leistungsportfolio um.",
    "Sie wollen uns in einem unserer Themengebiete 'auf Zeit' engagieren? Dann finden Sie hier alle Details.",
    "Sie würden gerne unser Know-how für reine Trainings und/oder Coachings abfragen? Dann schauen Sie sich hier um.",
  ],
  readMore: "Mehr erfahren",
};

const renderWithBold = (text: string) => {
  const parts = text ? text.split(COMPANY_NAME) : [];
  return parts.map((part, i) => (
    <span key={i}>
      {part}
      {i < parts.length - 1 && (
        <strong className="text-primary-600">{COMPANY_NAME}</strong>
      )}
    </span>
  ));
};

export default function ServicesDePage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Unsere Services | NetBasis International"
        description="Umfassende kaufmännische und technische Dienstleistungen, Beratung und Training für Ihre erfolgreiche Expansion in den DACH-Markt."
        ogUrl="https://net-basis.net/de/services"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <ServicesSlider />

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            {/* WIDGET 1 - Überschrift (h2) */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {texts.title}
            </h2>

            {/* WIDGET 2 - H1: fetter Fließtext */}
            <h1 className="text-foreground-950 font-semibold text-sm md:text-base leading-relaxed mb-4">
              {texts.h1Text}
            </h1>

            {/* WIDGET 3 - normaler Text */}
            <div className="space-y-4 font-body text-sm md:text-base text-foreground-950 leading-relaxed">
              {texts.introParagraphs.map(
                (text, idx) =>
                  text && (
                    <p key={idx}>{renderWithBold(text)}</p>
                  )
              )}
            </div>
          </div>
        </section>

        {/* Service Cards */}
        <ServiceCards
          namespace="services"
          titleKeys={[]}
          descriptionKeys={[]}
          readMoreKey=""
          texts={cardTexts}
        />

        {/* WIDGET 4 + 5 - grüne h2 + Absatz */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {texts.sectionTitle}
            </h2>
            <div className="space-y-4 font-body text-sm md:text-base text-foreground-950 leading-relaxed">
              <p>{renderWithBold(texts.belowText)}</p>
            </div>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {texts.claim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}